import moment from 'moment';
import { playlistTypes } from '@/constants';
import { defineStore } from 'pinia';

const RENDER_DELAY_BUFFER = 10000;
const DUPE_CHECK_DELAY = { amount: 5, unit: 'minutes' };
const renderMessages = [
    'Gathering your memories.',
    'Matching music to the moments.',
    'Adding a touch of magic.',
    'Turning photos into stories.',
    'Creating a timeless tribute.',
    'Perfecting the transitions.',
    'Synchronizing music and slides.',
    'Almost done crafting your masterpiece.',
    'Bringing your cherished moments to life.',
    'Making it picture-perfect.',
];
function secondsToDisplayTime(input) {
    const duration = moment.duration(input, 'seconds');
    const hours = duration.hours();
    const minutes = duration.minutes();
    const seconds = duration.seconds();
    const formattedMinutes = String(minutes).padStart(2, '0');
    const formattedSeconds = String(seconds).padStart(2, '0');
    function toDisplayString() {
        if (hours > 0) {
            return `${hours}:${formattedMinutes}:${formattedSeconds}`;
        }
        return `${formattedMinutes}:${formattedSeconds}`;
    }
    return { hours, minutes, seconds, formattedMinutes, formattedSeconds, toDisplayString };
}

export const useTributeVideoStore = defineStore('tributeVideo', {
    state: () => ({
        id: null,
        externalId: null,
        funeralHomeId: null,
        funeralHomeName: null,
        funeralHomeLogo: null,
        ownerName: null,
        eventId: null,
        ownerId: null,
        serviceId: null,
        serviceSlug: null,
        storyEventId: null,
        storyEvent: null,
        loading: false,
        uploadingPhotos: false,
        rotatingPhotos: false,
        photos: [],
        photosOptions: {
            pageNum: 0,
            pageSize: 24,
        },
        selectedPhotos: [],
        selectedTemplates: [],
        selectedSongs: [],
        originalPlaylist: [],
        publicPlaylist: [],
        age: null,
        deeplink: null,
        familyLink: null,
        storyContributeLink: null,
        deadline: null,
        mainPhotoId: null,
        mainPhotoUrl: null,
        faceUuid: null,
        render: null,
        firstName: null,
        lastName: null,
        birthDate: null,
        deathDate: null,
        totalPhotos: 0,
        autoSlideDuration: true,
        slideDuration: 0,
        introDuration: 0,
        selectedAnimation: 0,
        selectedTransition: 0,
        minPhotos: 1,
        incomingUploadCount: 0,
        endingType: 1,
        endingDuration: 0,
        endingDurationTotal: 0,
        endingSlateDuration: 0,
        endingVideoDuration: 0,
        videoSlidesDuration: 0,
        videoSlidesCount: 0,
        uploadsOnly: false,
        separatePlayer: false,
        audioRenderRequired: false,
        previewJsonRefreshed: null,
        maxItems: 0,
        maxSessionItems: null,
        renderStarted: null,
        totalDuration: 0,
        totalSlidesCount: 0,
        renderCompleted: null,
        overlayDismissed: false,
        lastSyncedChange: null,
        contributorDetailsSet: false,
        profileUploadProgress: 0,
        showProfileUploadProgress: false,
        pendingProfileUpload: null,
        renderProgress: 0,
        renderStarted: null,
        renderCompleted: false,
        currentMessageIndex: 0,
        renderMessageInterval: null,
        renderProgressInterval: null,
        duplicateSlideCount: 0,
        similarSlideCount: 0,
        slideCountWhenLastDupeCheck: -1,
        lastDupeCheckTimestamp: null,
        autoRenderAttempts: 0,
        // dupe/similar slide handling
        duplicateSlideGroups: {
            results: [],
            total: 0,
        },
        similarSlideGroups: {
            results: [],
            total: 0,
        },
        tributeWorkflow: 0,
        ingestPipelineCount: 0,
        submissionsOpen: true,
        tributeEndingVideo: null,
        WebSlideshowSlideSeconds: null,
        outroSlate: false,
        outroBrandedVideo: false,
        replaceOutroSlateWithThemeDuplicate: false,
        performanceMode: 0,
    }),
    actions: {
        setPerformanceMode(payload) {
            this.performanceMode = payload;
        },
        setRenderProgress(payload) {
            this.renderProgress = payload;
        },
        setRenderStarted(payload) {
            this.renderStarted = payload;
        },
        updateRenderProgress(payload) {
            const { started, duration } = payload;

            if (!duration) return;

            this.setRenderStarted(started);
            this.renderProgressInterval = setInterval(() => {
                const now = moment().utc();
                const start = moment.utc(this.renderStarted);
                const elapsed = now.diff(start, 'seconds');

                let progress = (elapsed / duration) * 100;

                progress = progress > 100 ? 100 : Math.floor(progress);

                if (progress < 0) {
                    clearInterval(this.renderProgressInterval);
                    this.setRenderProgress(0);
                    return;
                }

                this.setRenderProgress(progress);
                if (progress >= 100) {
                    clearInterval(this.renderProgressInterval);
                }
            }, 400);
        },
        cycleRenderMessages() {
            const messageInterval = setInterval(() => {
                let newIndex = this.currentMessageIndex + 1;

                if (newIndex > renderMessages.length - 1) {
                    newIndex = 0;
                }

                this.setCurrentMessageIndex(newIndex);
            }, 4000);

            this.setMessageInterval(messageInterval);
        },
        clearIntervals() {
            this.clearRenderMessageInterval();
            this.clearRenderProgressInterval();
        },
        clearRenderProgressInterval() {
            if (this.renderProgressInterval) {
                clearInterval(this.renderProgressInterval);
            }

            this.renderProgressInterval = null;
        },
        clearRenderMessageInterval() {
            if (this.renderMessageInterval) {
                clearInterval(this.renderMessageInterval);
            }

            this.renderMessageInterval = null;
        },
        setRenderCompleted() {
            this.renderCompleted = new Date().toISOString();
            this.renderProgress = 100;
            this.overlayDismissed = true;
        },
        setCurrentMessageIndex(payload) {
            this.currentMessageIndex = payload;
        },
        setMessageInterval(payload) {
            this.renderMessageInterval = payload;
        },
        setProgressInterval(payload) {
            this.renderProgressInterval = payload;
        },
        updatePendingProfileUpload(payload) {
            this.pendingProfileUpload = payload;
        },
        updateProfileUploadProgress(payload) {
            this.profileUploadProgress = payload;
        },
        updateShowProfileUploadProgress(payload) {
            this.showProfileUploadProgress = payload;
        },
        updateUploadingPhotos(payload) {
            this.uploadingPhotos = payload;
        },
        updateGalleryItem(payload) {
            // Find the gallery item in the state by ID
            const foundItem = this.photos.find(item => item.id === payload.id);

            if (foundItem) {
                // Update the properties of the found item
                for (const key in payload.data) {
                    if (Object.hasOwnProperty.call(payload.data, key)) {
                        foundItem[key] = payload.data[key];
                        foundItem.refreshKey++;
                    }
                }
            }
        },
        updateTributeVideoPhotosOptions(payload) {
            // Checking if the payload values for pageNum and pageSize are numbers and are 0 or greater
            if (typeof payload.pageNum === 'number' && payload.pageNum >= 0) {
                this.photosOptions.pageNum = payload.pageNum;
            }

            if (typeof payload.pageSize === 'number' && payload.pageSize >= 0) {
                this.photosOptions.pageSize = payload.pageSize;
            }
        },
        updateTributeVideoSelectedSongs(payload) {
            this.selectedSongs = payload;
        },
        updateTributeVideoPlaylist(payload) {
            if (payload.playlistType === playlistTypes.ORIGINAL) {
                this.selectedSongs = payload.songs;
                this.originalPlaylist = payload.songs;
                return;
            }

            if (payload.playlistType === playlistTypes.PUBLIC) {
                this.publicPlaylist = payload.songs;
                return;
            }
        },
        updateTributeVideoSelectedTemplates(payload) {
            this.selectedTemplates = payload;
        },
        updateTributeVideoSelectedPhotos(payload) {
            this.selectedPhotos = payload;
        },
        updateTributeVideoPhotos(payload) {
            this.photos = payload;
        },
        updateTributeVideo(payload) {
            // TODO: revisit this here, where `this` used to be `state`
            this.$patch(payload);
            // Object.assign(this, payload);
        },
        setTributeVideoRenderState(payload) {
            this.renderStarted = payload.startTime;
        },
        resetTributeVideoState() {
            // Using a built-in method to reset see: https://pinia.vuejs.org/core-concepts/this.html#Resetting-the-state
            this.$reset();
        },
        dismissRenderOverlay() {
            this.overlayDismissed = true;
        },
    },
    getters: {
        minimumCreateRequirementsMet() {
            if (this?.totalSlidesCount?.length < 1) {
                return false;
            }
            if (this?.selectedTemplates?.length < 1) {
                return false;
            }

            if (!this?.mainPhotoId > 0) {
                return false;
            }

            if (this?.incomingUploadCount > 0) {
                return false;
            }

            if (this?.ingestPipelineCount > 0) {
                return false;
            }

            if (this?.rotatingPhotos) {
                return false;
            }

            return true;
        },
        topThreeSelectedPhotos() {
            return this.selectedPhotos.filter(x => x.mediaType !== 1).slice(0, 3);
        },
        topThreeSelectedSongs() {
            return this.selectedSongs.slice(0, 3);
        },
        selectedPhotoCount() {
            return this.selectedPhotos.length;
        },
        selectedSongCount() {
            return this.selectedSongs.length;
        },
        selectedTemplateCount() {
            return this.selectedTemplates.length;
        },
        // alias of Templates
        selectedThemeCount() {
            return this.selectedTemplates.length;
        },
        selectedTheme() {
            // assumes only one theme/template can beselected at once
            return this.selectedTemplates[0];
        },
        tributeIsRendering() {
            if (this.overlayDismissed) {
                return false;
            }
            if (this.renderStarted && !this.renderCompleted) {
                return moment().isBefore(moment(this.renderStarted).add(this.totalDuration, 'seconds'));
            }
            return false;
        },
        delayRenderCallByMilliseconds() {
            // Workaround a known issue where image changes aren't ready by the time you start a render, leading to a failed render
            // We're front-loading a 10 second buffer. So the user will see the loading UI, but render call won't happen until after that initial delay
            // to make this robust we'll need to make sure that the call is executed before a user tries to leave/refresh/etc.
            if (!this.lastSyncedChange) {
                return 0;
            }
            const now = moment(),
                then = moment(this.lastSyncedChange).add(RENDER_DELAY_BUFFER, 'milliseconds');
            // Take the lastSyncedChange timestamp and add the buffer to it, return the amount of delay or 0
            if (now.isBefore(then)) {
                return Math.ceil(then.diff(now, 'milliseconds', true));
            }
            return 0;
        },
        deadlineToLocal() {
            if (this?.deadline?.date || this?.deadline) {
                return moment.utc(this?.deadline?.date || this?.deadline).local();
            }
            return null;
        },
        deadlineExpired() {
            const deadline = moment.utc(this?.deadline?.date || this?.deadline);
            if (deadline.isValid()) {
                return moment().isAfter(deadline.local());
            }
            return false;
        },
        currentRenderMessage() {
            return renderMessages[this.currentMessageIndex];
        },
        renderStateText() {
            if (this.renderCompleted) {
                return 'Render finished!';
            }
            if (this.renderProgress >= 100) {
                return 'Just a few more minutes...';
            }
            return 'Rendering your tribute video...';
        },
        formattedTributeDuration() {
            const { toDisplayString } = secondsToDisplayTime(Math.ceil(this.totalDuration));
            return toDisplayString();
        },
        formattedSlidesDuration() {
            // TODO: Here we only care about the total for the slides...
            // but video slides can throw off that calculation
            // Need to subtract intro, outro and branded outro if
            const { toDisplayString } = secondsToDisplayTime(
                Math.ceil(this.totalDuration - this.introDuration - this.endingDurationTotal),
            );
            return toDisplayString();
        },
        formattedTributeEndingVideoDuration() {
            const { toDisplayString } = secondsToDisplayTime(Math.ceil(this.endingVideoDuration));
            return toDisplayString();
        },
        formattedTributeEndingSlateDuration() {
            const { toDisplayString } = secondsToDisplayTime(Math.ceil(this.endingSlateDuration));
            return toDisplayString();
        },
        estimatedRenderDuration() {
            const { hours, minutes, seconds } = secondsToDisplayTime(Math.round((this.totalDuration * 2) / 60) * 60);

            if (hours) {
                if (minutes && seconds) {
                    return `${hours} ${hours === 1 ? 'hour' : 'hours'},
                        ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}
                        and ${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
                }
                if (minutes) {
                    return `${hours} ${hours === 1 ? 'hour' : 'hours'},
                        and ${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
                }
            }
            if (minutes && seconds) {
                return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}
                        and ${seconds} ${seconds === 1 ? 'second' : 'seconds'}`;
            }

            return `${minutes} ${minutes === 1 ? 'minute' : 'minutes'}`;
        },
        dueForADupeCheck() {
            // if a dupe check hasn't been performed yet
            if (!this.lastDupeCheckTimestamp || !moment(this.lastDupeCheckTimestamp).isValid()) return true;

            // If the slide counts don't match
            if (this.totalSlidesCount !== this.slideCountWhenLastDupeCheck) return true;

            // It's been 5 minutes since the last dupe check
            return moment().isAfter(
                moment(this.lastDupeCheckTimestamp).add(DUPE_CHECK_DELAY.amount, DUPE_CHECK_DELAY.unit),
            );
        },
    },
});
